import React, { useState, useMemo } from 'react';
import { useShop } from '../context/ShopContext';
import ProductCard from '../components/ProductCard';
import SkeletonCard from '../components/SkeletonCard';
import { Icon } from '@iconify/react';

const Home: React.FC = () => {
  const { products, isLoading } = useShop();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  // Derive categories from products
  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map(p => p.category)));
    return ['All', ...cats];
  }, [products]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Structured Data for Product List
  const productListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": filteredProducts.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.image,
        "sku": product.id,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "GHS",
          "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      }
    }))
  };

  return (
    <div className="space-y-8 md:space-y-12">
      <script type="application/ld+json">
        {JSON.stringify(productListSchema)}
      </script>
      {/* Hero Section */}
      {/* Hero Section - Stitch Design */}
      {/* Hero Section - Stitch Design */}
      <section className="p-4">
        <div className="relative overflow-hidden rounded-xl bg-primary/5 min-h-[520px] flex items-end group">

          {/* LCP Optimized Background Image */}
          <img
            src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=1200"
            srcSet="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=600 600w,
                    https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=1200 1200w,
                    https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=2000 2000w"
            alt="Fashion Collection"
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            fetchPriority="high"
            loading="eager"
          />

          {/* Gradient Overlay - Light for Black Text Contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/40 to-transparent pointer-events-none" />

          {/* Content */}
          <div className="relative z-10 p-6 space-y-3 font-display w-full">
            <span className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white bg-mauve-dark/90 rounded-full">New Season</span>
            <h1 className="text-4xl font-extrabold leading-tight text-black">
              Embrace Your <br /><span className="text-black/80 bg-clip-text">Elegance</span>
            </h1>
            <p className="text-sm font-medium text-black/80 max-w-[240px]">
              Discover our curated collection of soft pastels and flowing silks.
            </p>
            <div className="pt-4">
              <button
                onClick={() => {
                  const element = document.getElementById('shop-grid');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 rounded-lg bg-mauve-dark px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-mauve-dark/25 active:scale-95 transition-all hover:bg-mauve-medium"
              >
                Explore Now
                <Icon icon="lucide:arrow-right" width="20" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <div id="shop-grid" className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-mauve-light/20 sticky top-16 md:top-20 z-30">

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 justify-center md:justify-start w-full md:w-auto overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all whitespace-nowrap ${selectedCategory === cat
                ? 'bg-mauve-dark text-white shadow-md'
                : 'bg-mauve-base/50 text-mauve-text hover:bg-mauve-light/50'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-auto">
          <Icon icon="lucide:search" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-mauve-medium" width="18" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-64 pl-10 pr-4 py-2 bg-mauve-base/30 border border-mauve-medium/30 rounded-full focus:outline-none focus:ring-2 focus:ring-mauve-medium/50 text-mauve-text text-sm"
            aria-label="Search collections"
          />
        </div>
      </div>

      {/* Product Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-8">
          {[...Array(8)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-8">
          {filteredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} priority={index < 4} />
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <Icon icon="lucide:filter" width="48" className="mx-auto text-mauve-medium mb-4" />
          <h3 className="text-xl font-medium text-mauve-text">No products found</h3>
          <p className="text-mauve-medium">Try adjusting your search or filter.</p>
        </div>
      )}
    </div>
  );
};

// Simple Loading Component
const LoadingSkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-8">
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-mauve-light/20 animate-pulse">
        <div className="h-[280px] bg-mauve-base/20" />
        <div className="p-4 space-y-3">
          <div className="h-4 bg-mauve-base/20 rounded w-3/4" />
          <div className="h-4 bg-mauve-base/20 rounded w-1/2" />
          <div className="flex justify-between items-center pt-2">
            <div className="h-6 bg-mauve-base/20 rounded w-1/3" />
            <div className="h-8 bg-mauve-base/20 rounded-full w-8" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default Home;